import React, { useState } from "react";
import { motion } from "framer-motion";
import { MapPin, Phone, Facebook, Menu, X, Info, MessageCircle, CalendarCheck } from "lucide-react";
import { BIZ, HIGHLIGHTS, PRODUCTS, TIMELINE, STEPS, REVIEWS } from "./typohuatData";
import ConceptCTAPill from "@/components/ConceptCTAPill";

const BRASS = "#B08D3F";
const WOOD = "#1E1712";
const CREAM = "#F6EFE2";

const fadeUp = { initial: { opacity: 0, y: 22 }, whileInView: { opacity: 1, y: 0 }, viewport: { once: true, margin: "-60px" }, transition: { duration: 0.6, ease: "easeOut" } };

export default function TyPoHuatSite() {
  const [open, setOpen] = useState(false);
  const links = [["Heritage", "#heritage"], ["Craft", "#craft"], ["Reviews", "#reviews"], ["Visit", "#visit"]];

  return (
    <div id="top" className="min-h-screen" style={{ background: CREAM, color: "#241C10", fontFamily: "'Cormorant Garamond', serif" }} data-testid="typohuat-concept-root">
      <style>{`@import url('https://fonts.googleapis.com/css2?family=Cormorant+Garamond:wght@400;500;600;700&family=Jost:wght@400;500;600&display=swap'); .tph-sans{font-family:'Jost',sans-serif;}`}</style>

      <div data-testid="jewelry-disclaimer" className="bg-black text-white text-center text-xs tph-sans py-2.5">
        <span className="inline-flex items-center gap-2"><Info size={13} style={{ color: BRASS }} />Concept website created by <a href="https://mysitecare.eu/ph" target="_blank" rel="noopener noreferrer" className="underline decoration-white/40 hover:decoration-white">MySiteCare</a> — not currently operated by or affiliated with {BIZ.name}.</span>
      </div>

      <header className="sticky top-0 z-40" style={{ background: WOOD }}>
        <nav className="max-w-6xl mx-auto px-5 sm:px-8 h-20 flex items-center justify-between">
          <a href="#top" data-testid="typohuat-logo" className="text-2xl" style={{ color: CREAM }}>Ty Po Huat</a>
          <div className="hidden md:flex items-center gap-8 tph-sans">
            {links.map(([l, h]) => <a key={h} href={h} className="text-sm" style={{ color: "#D8CBAE" }}>{l}</a>)}
          </div>
          <a href={BIZ.bookingLink} target="_blank" rel="noopener noreferrer" data-testid="typohuat-header-cta" className="hidden sm:inline-flex tph-sans px-6 py-2.5 text-sm font-semibold" style={{ background: BRASS, color: WOOD }}>Book a Consultation</a>
          <button className="md:hidden" style={{ color: CREAM }} onClick={() => setOpen(!open)} data-testid="typohuat-menu-toggle" aria-label="Menu">{open ? <X size={24} /> : <Menu size={24} />}</button>
        </nav>
        {open && <div className="md:hidden px-5 pb-4 space-y-1 tph-sans" style={{ background: WOOD }}>{links.map(([l, h]) => <a key={h} href={h} onClick={() => setOpen(false)} className="block py-2.5" style={{ color: "#D8CBAE" }}>{l}</a>)}</div>}
      </header>

      {/* HERO — typographic, heritage-led, no photo needed */}
      <section className="relative py-28 sm:py-36 text-center overflow-hidden" style={{ background: WOOD }} data-testid="typohuat-hero">
        <div className="absolute inset-0 opacity-[0.05]" style={{ backgroundImage: "repeating-linear-gradient(135deg, white 0, white 1px, transparent 1px, transparent 26px)" }} />
        <div className="relative max-w-3xl mx-auto px-5 sm:px-8">
          <motion.div {...fadeUp}>
            <span className="tph-sans text-xs uppercase tracking-[0.3em]" style={{ color: BRASS }}>Ongpin Heritage Goldsmith</span>
            <h1 className="mt-6 text-6xl sm:text-8xl leading-none" style={{ color: CREAM }}>1949</h1>
            <p className="mt-6 text-2xl" style={{ color: "#D8CBAE" }}>Three generations of goldsmithing, one family, one street.</p>
            <div className="mt-10 flex flex-wrap justify-center gap-4 tph-sans">
              <a href={BIZ.bookingLink} target="_blank" rel="noopener noreferrer" data-testid="typohuat-hero-book" className="px-8 py-3.5 text-sm font-semibold" style={{ background: BRASS, color: WOOD }}>Book a Consultation</a>
              <a href={BIZ.facebook} target="_blank" rel="noopener noreferrer" data-testid="typohuat-hero-message" className="px-8 py-3.5 text-sm font-semibold border" style={{ borderColor: "#D8CBAE", color: CREAM }}>Message Us</a>
            </div>
            <p className="mt-8 tph-sans text-sm" style={{ color: "#8A7A57" }}>{BIZ.rating}★ Local Rating · {BIZ.reviewCount} Public Reviews</p>
          </motion.div>
        </div>
      </section>

      {/* HERITAGE TIMELINE — the centerpiece */}
      <section id="heritage" className="max-w-4xl mx-auto px-5 sm:px-8 py-24">
        <motion.h2 {...fadeUp} className="text-4xl text-center mb-16">A Family Trade, Passed Down</motion.h2>
        <div className="relative pl-8 border-l-2" style={{ borderColor: BRASS }}>
          {TIMELINE.map((t) => (
            <motion.div key={t.year} {...fadeUp} data-testid="typohuat-timeline-item" className="relative mb-12 last:mb-0">
              <span className="absolute -left-[41px] top-1 w-4 h-4 rounded-full" style={{ background: BRASS }} />
              <span className="tph-sans text-xs uppercase tracking-widest" style={{ color: BRASS }}>{t.year}</span>
              <p className="mt-1 text-xl leading-relaxed">{t.text}</p>
            </motion.div>
          ))}
        </div>
      </section>

      {/* CRAFT — maker's-mark verified product photography */}
      <section id="craft" className="py-24" style={{ background: "#EDE2C8" }}>
        <div className="max-w-6xl mx-auto px-5 sm:px-8">
          <motion.div {...fadeUp} className="text-center mb-14">
            <span className="tph-sans text-xs uppercase tracking-[0.25em]" style={{ color: "#8A6B2A" }}>Our Craftsmanship</span>
            <h2 className="mt-3 text-4xl">Goldsmithed by hand, marked as ours</h2>
          </motion.div>
          <div className="grid sm:grid-cols-2 md:grid-cols-3 gap-6">
            {PRODUCTS.map((p) => (
              <motion.div key={p.name} {...fadeUp} data-testid="typohuat-product-card" className="bg-white shadow-sm">
                <img src={p.img} alt={p.name} className="w-full h-56 object-cover" />
                <div className="p-5">
                  <h3 className="text-lg">{p.name}</h3>
                  <p className="mt-1 tph-sans text-xs text-[#5A4A2E]/80">{p.desc}</p>
                  <a href={BIZ.facebook} target="_blank" rel="noopener noreferrer" className="mt-3 inline-block tph-sans text-xs font-semibold" style={{ color: BRASS }}>Ask About This Piece &rarr;</a>
                </div>
              </motion.div>
            ))}
          </div>
          <div className="mt-14 grid sm:grid-cols-4 gap-6 tph-sans">
            {HIGHLIGHTS.map((h) => (
              <motion.div key={h.title} {...fadeUp} className="text-center px-4">
                <p className="font-semibold text-sm">{h.title}</p>
                <p className="mt-1.5 text-xs text-[#5A4A2E]/70">{h.text}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* REVIEWS */}
      <section id="reviews" className="max-w-5xl mx-auto px-5 sm:px-8 py-24">
        <motion.h2 {...fadeUp} className="text-4xl text-center mb-14">What Customers Say</motion.h2>
        <div className="grid md:grid-cols-3 gap-8 tph-sans">
          {REVIEWS.map((r) => (
            <motion.div key={r.name} {...fadeUp} className="p-7 border-t-2" style={{ borderColor: BRASS }}>
              <p className="text-[#3A2E1A]/85 leading-relaxed text-sm">"{r.text}"</p>
              <p className="mt-4 text-xs font-semibold" style={{ color: "#8A6B2A" }}>{r.name} <a href={r.sourceUrl} target="_blank" rel="noopener noreferrer" className="text-[#A8926B] font-normal underline underline-offset-2">· {r.source}</a></p>
            </motion.div>
          ))}
        </div>
      </section>

      {/* STEPS */}
      <section className="py-20" style={{ background: WOOD }}>
        <div className="max-w-4xl mx-auto px-5 sm:px-8 grid sm:grid-cols-4 gap-6 tph-sans text-center">
          {STEPS.map((s) => (
            <motion.div key={s.n} {...fadeUp}>
              <div className="text-3xl" style={{ color: BRASS }}>{s.n}</div>
              <p className="mt-2 font-semibold" style={{ color: CREAM }}>{s.title}</p>
              <p className="mt-1 text-xs" style={{ color: "#A8926B" }}>{s.text}</p>
            </motion.div>
          ))}
        </div>
      </section>

      {/* VISIT */}
      <section id="visit" className="max-w-6xl mx-auto px-5 sm:px-8 py-24 grid md:grid-cols-2 gap-12">
        <motion.div {...fadeUp}>
          <h2 className="text-4xl">Visit Us</h2>
          <ul className="mt-8 space-y-5 tph-sans">
            <li className="flex items-start gap-4"><MapPin size={20} style={{ color: BRASS }} className="mt-0.5" />{BIZ.address}</li>
            <li className="flex items-start gap-4"><Phone size={20} style={{ color: BRASS }} className="mt-0.5" /><a href={`tel:${BIZ.bookingPhone.replace(/\s/g, "")}`} className="hover:underline">{BIZ.bookingPhone}</a></li>
            <li className="flex items-start gap-4"><Facebook size={20} style={{ color: BRASS }} className="mt-0.5" /><a href={BIZ.facebook} target="_blank" rel="noopener noreferrer" data-testid="typohuat-facebook-link" className="hover:underline">typohuatgoldsmith</a></li>
            <li className="flex items-start gap-4"><CalendarCheck size={20} style={{ color: BRASS }} className="mt-0.5" /><a href={BIZ.bookingLink} target="_blank" rel="noopener noreferrer" data-testid="typohuat-booking-link" className="hover:underline">Book online via Setmore</a></li>
          </ul>
        </motion.div>
        <motion.div {...fadeUp} className="min-h-[320px]"><iframe title="Ty Po Huat Goldsmith map" src={BIZ.mapEmbed} className="w-full h-full min-h-[320px]" style={{ border: 0 }} loading="lazy" referrerPolicy="no-referrer-when-downgrade" /></motion.div>
      </section>

      <footer className="py-8 tph-sans" style={{ background: WOOD, color: "#8A7A57" }}>
        <div className="max-w-6xl mx-auto px-5 sm:px-8 flex flex-col sm:flex-row justify-between gap-3 text-xs">
          <p className="text-base" style={{ color: CREAM, fontFamily: "'Cormorant Garamond', serif" }}>Ty Po Huat Goldsmith</p>
          <p>This is an unsolicited concept website by MySiteCare. Not operated by or affiliated with {BIZ.name}. Confirm pricing in person.</p>
        </div>
      </footer>

      <a href={BIZ.facebook} target="_blank" rel="noopener noreferrer" data-testid="typohuat-sticky-contact" className="md:hidden fixed bottom-4 inset-x-4 z-50 tph-sans px-6 py-3.5 text-center text-sm font-semibold shadow-xl flex items-center justify-center gap-2" style={{ background: BRASS, color: WOOD }}><MessageCircle size={17} /> Message Ty Po Huat</a>
      <ConceptCTAPill slug="ty-po-huat-goldsmith-concept" />
    </div>
  );
}
